import { useMemo, useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';

import { useModal } from '@/hooks/useModal';
import { useAvailableInvestorsForFund } from '@/hooks/react-query/useInvestors';
import { formatCurrencyNoDecimals } from '@/utils/formatters';
import type { Fund } from '@/types/fund';
import type { InvestorDetail } from '@/types/investor';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CurrencyInput } from '@/components/ui/currency-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MAX_TRANSFER_TRANSACTION_NAME_LENGTH,
  type InvestorFundTransferDraftPayload,
  type TransferFormData,
} from '@/types/investor-transfer';

import type { TransfereeCandidate } from './investorTransferUtils';
import {
  buildTransfereeCandidates,
  getAvailableTransfereesForInvestor,
  getRelationshipForFund,
  getTransferorCandidates,
} from './investorTransferUtils';

interface InvestorTransferModalProps {
  open: boolean;
  fund: Fund;
  investors: InvestorDetail[];
  loading: boolean;
  onClose: () => void;
  onInitiateTransfer?: (
    payload: InvestorFundTransferDraftPayload
  ) => Promise<void> | void;
  onSuccess?: () => void;
}

function getTodayDateString(): string {
  const now = new Date();
  const timezoneOffsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
}

function truncateToInteger(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }
  return Math.trunc(value);
}

function buildInitialTransferFormData(): TransferFormData {
  return {
    transaction_name: '',
    transfer_type: 'full',
    effective_date: getTodayDateString(),
    transferee_allocations: [],
  };
}

export function InvestorTransferModal({
  open,
  fund,
  investors,
  loading,
  onClose,
  onInitiateTransfer,
  onSuccess,
}: InvestorTransferModalProps) {
  const [transferError, setTransferError] = useState<string | null>(null);
  const [isInitiatingTransfer, setIsInitiatingTransfer] = useState(false);
  const [transferorInvestorId, setTransferorInvestorId] = useState('');
  const [transfereeSearch, setTransfereeSearch] = useState('');
  const [transferFormData, setTransferFormData] = useState<TransferFormData>(
    buildInitialTransferFormData
  );

  const isTransferConfigured = Boolean(onInitiateTransfer);

  const transferorCandidates = useMemo(
    () => getTransferorCandidates(investors, fund.id),
    [fund.id, investors]
  );

  const transferringInvestor =
    transferorCandidates.find(
      (candidate) => candidate.id === transferorInvestorId
    ) ||
    transferorCandidates[0] ||
    null;

  const transferringRelationship = transferringInvestor
    ? getRelationshipForFund(transferringInvestor, fund.id) || null
    : null;

  // A commitment can be transferred onto a fund, which opens the link on the
  // far side, so fund proxies belong in the transferee list.
  const investorsOutsideFundQuery = useAvailableInvestorsForFund(
    fund.id,
    'include'
  );

  const transfereeCandidates = buildTransfereeCandidates(
    investors,
    investorsOutsideFundQuery.data || []
  );

  const availableTransferees = transferringInvestor
    ? getAvailableTransfereesForInvestor(
        transferringInvestor.id,
        transfereeCandidates
      )
    : [];

  const normalizedTransferorCommitmentAmountNumeric =
    transferringRelationship?.commitment_amount ?? 0;

  const selectedTransfereeInvestorIds = new Set(
    transferFormData.transferee_allocations.map(
      (allocation) => allocation.transferee_investor_id
    )
  );

  const selectableTransferees = availableTransferees.filter(
    (candidate) => !selectedTransfereeInvestorIds.has(candidate.id)
  );

  const normalizedTransfereeSearch = transfereeSearch.trim().toLowerCase();

  const matchingTransferees = selectableTransferees.filter((candidate) =>
    candidate.name.toLowerCase().includes(normalizedTransfereeSearch)
  );

  const transfereeOptions = [
    ...matchingTransferees.filter(
      (candidate) => candidate.isExistingFundInvestor
    ),
    ...matchingTransferees.filter(
      (candidate) => !candidate.isExistingFundInvestor
    ),
  ];

  const totalAllocatedCommitmentAmountNumeric =
    transferFormData.transferee_allocations
      .map((allocation) => allocation.transfer_commitment_amount ?? 0)
      .filter((amount) => amount > 0)
      .reduce((sum, amount) => sum + amount, 0);

  const remainingTransferAmountNumeric = Math.max(
    normalizedTransferorCommitmentAmountNumeric -
      totalAllocatedCommitmentAmountNumeric,
    0
  );

  const formatCurrency = (
    amount: number | string | null | undefined,
    currency = 'EUR'
  ) => {
    return formatCurrencyNoDecimals(amount, currency);
  };

  const handleClose = () => {
    if (isInitiatingTransfer) return;
    setTransferFormData(buildInitialTransferFormData());
    setTransferError(null);
    setIsInitiatingTransfer(false);
    setTransferorInvestorId('');
    onClose();
  };

  const { modalProps } = useModal({
    isOpen: open,
    onClose: handleClose,
  });

  const handleTransferorChange = (nextTransferorInvestorId: string) => {
    setTransferorInvestorId(nextTransferorInvestorId);

    const selectedTransferor =
      transferorCandidates.find(
        (candidate) => candidate.id === nextTransferorInvestorId
      ) || null;

    const selectedRelationship = selectedTransferor
      ? getRelationshipForFund(selectedTransferor, fund.id)
      : null;

    const transferorCommitmentAmount = truncateToInteger(
      selectedRelationship?.commitment_amount
    );

    setTransferFormData((previous) => ({
      ...previous,
      transferee_allocations:
        previous.transfer_type === 'full'
          ? []
          : previous.transferee_allocations.map((allocation) => ({
              ...allocation,
              transfer_commitment_amount:
                allocation.transfer_commitment_amount != null &&
                transferorCommitmentAmount != null
                  ? truncateToInteger(
                      Math.min(
                        allocation.transfer_commitment_amount,
                        transferorCommitmentAmount
                      )
                    )
                  : allocation.transfer_commitment_amount,
            })),
    }));

    setTransferError(null);
  };

  const handleTransferTypeChange = (transferType: 'full' | 'partial') => {
    setTransferFormData((previous) => ({
      ...previous,
      transfer_type: transferType,
      transferee_allocations:
        transferType === 'full' &&
        previous.transferee_allocations.length === 1 &&
        normalizedTransferorCommitmentAmountNumeric > 0
          ? [
              {
                ...previous.transferee_allocations[0],
                transfer_commitment_amount: truncateToInteger(
                  normalizedTransferorCommitmentAmountNumeric
                ),
              },
            ]
          : previous.transferee_allocations,
    }));

    setTransferError(null);
  };

  const handleAddTransfereeAllocation = (candidate: TransfereeCandidate) => {
    const defaultAmount =
      transferFormData.transfer_type === 'full'
        ? truncateToInteger(remainingTransferAmountNumeric)
        : null;

    setTransfereeSearch('');

    setTransferFormData((previous) => ({
      ...previous,
      transferee_allocations: [
        ...previous.transferee_allocations,
        {
          transferee_investor_id: candidate.id,
          transfer_commitment_amount: defaultAmount,
        },
      ],
    }));

    setTransferError(null);
  };

  const handleTransferAmountChange = (
    transfereeInvestorId: string,
    value: number | null
  ) => {
    setTransferFormData((previous) => ({
      ...previous,
      transferee_allocations: previous.transferee_allocations.map(
        (allocation) =>
          allocation.transferee_investor_id === transfereeInvestorId
            ? {
                ...allocation,
                transfer_commitment_amount: truncateToInteger(value),
              }
            : allocation
      ),
    }));

    setTransferError(null);
  };

  const handleRemoveTransfereeAllocation = (transfereeInvestorId: string) => {
    setTransferFormData((previous) => ({
      ...previous,
      transferee_allocations: previous.transferee_allocations.filter(
        (allocation) =>
          allocation.transferee_investor_id !== transfereeInvestorId
      ),
    }));

    setTransferError(null);
  };

  const handleContinue = async () => {
    if (!transferringInvestor) {
      setTransferError('Select a transferor investor.');
      return;
    }

    if (!onInitiateTransfer) {
      setTransferError('Transfer action is currently unavailable.');
      return;
    }

    if (transferFormData.transferee_allocations.length === 0) {
      setTransferError('Add at least one transferee allocation.');
      return;
    }

    const transactionName = transferFormData.transaction_name.trim();

    if (transactionName.length > MAX_TRANSFER_TRANSACTION_NAME_LENGTH) {
      setTransferError(
        `Transaction name cannot exceed ${MAX_TRANSFER_TRANSACTION_NAME_LENGTH} characters.`
      );
      return;
    }

    if (!transferFormData.effective_date) {
      setTransferError('Effective date is required.');
      return;
    }

    const transferorRelationship = getRelationshipForFund(
      transferringInvestor,
      fund.id
    );

    if (!transferorRelationship) {
      setTransferError('Source fund relationship not found for this investor.');
      return;
    }

    const maxTransferAmount = transferorRelationship.commitment_amount ?? 0;

    if (maxTransferAmount <= 0) {
      setTransferError('Transferor commitment is not available for transfer.');
      return;
    }

    const transfereeAllocations = transferFormData.transferee_allocations.map(
      (allocation) => ({
        ...allocation,
        transfer_commitment_amount_numeric:
          allocation.transfer_commitment_amount ?? 0,
      })
    );

    const hasInvalidAmount = transfereeAllocations.some(
      (allocation) => allocation.transfer_commitment_amount_numeric <= 0
    );

    if (hasInvalidAmount) {
      setTransferError(
        'Each transferee allocation amount must be greater than 0.'
      );
      return;
    }

    const hasInvalidTransferee = transfereeAllocations.some(
      (allocation) =>
        !availableTransferees.some(
          (candidate) => candidate.id === allocation.transferee_investor_id
        )
    );

    if (hasInvalidTransferee) {
      setTransferError('One or more transferee investors are not eligible.');
      return;
    }

    const uniqueTransfereeCount = new Set(
      transfereeAllocations.map(
        (allocation) => allocation.transferee_investor_id
      )
    ).size;

    if (uniqueTransfereeCount !== transfereeAllocations.length) {
      setTransferError(
        'Each transferee investor can be allocated only once per transfer.'
      );
      return;
    }

    const totalTransferCommitmentAmount = transfereeAllocations.reduce(
      (sum, allocation) => sum + allocation.transfer_commitment_amount_numeric,
      0
    );

    if (
      Number.isFinite(maxTransferAmount) &&
      totalTransferCommitmentAmount > maxTransferAmount
    ) {
      setTransferError(
        'Transferee allocation cannot exceed 100% of the transferor commitment.'
      );
      return;
    }

    if (
      transferFormData.transfer_type === 'full' &&
      Math.abs(totalTransferCommitmentAmount - maxTransferAmount) > 0.01
    ) {
      setTransferError('Full transfer requires allocations to total 100%.');
      return;
    }

    setTransferError(null);
    setIsInitiatingTransfer(true);

    const payload: InvestorFundTransferDraftPayload = {
      fund_id: fund.id,
      transferor_investor_id: transferringInvestor.id,
      transferor_relationship_id: transferorRelationship.id,
      effective_date: transferFormData.effective_date,
      transfer_commitment_currency: transferorRelationship.commitment_currency,
      include_equalization: false,
      transaction_name: transactionName || undefined,
      transferee_allocations: transfereeAllocations.map((allocation) => ({
        transferee_investor_id: allocation.transferee_investor_id,
        transfer_commitment_amount:
          allocation.transfer_commitment_amount_numeric,
      })),
    };

    try {
      await onInitiateTransfer(payload);
      onSuccess?.();
      onClose();
    } catch (error) {
      const errorMessage =
        (error as { message?: string })?.message ||
        'Failed to initiate transfer';
      setTransferError(errorMessage);
    } finally {
      setIsInitiatingTransfer(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className="max-h-[90vh] max-w-5xl overflow-y-auto p-0"
        {...modalProps}
      >
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>
            Transaction - Investor Transfers | {fund.display_name || fund.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Transfer capital account position between investors within this
            fund.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 px-6 py-4">
          {transferError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <span>{transferError}</span>
            </Alert>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="transfer_transaction_name">
                Transaction Name
              </Label>
              <Input
                id="transfer_transaction_name"
                value={transferFormData.transaction_name}
                onChange={(event) =>
                  setTransferFormData((previous) => ({
                    ...previous,
                    transaction_name: event.target.value,
                  }))
                }
                maxLength={MAX_TRANSFER_TRANSACTION_NAME_LENGTH}
                placeholder="Transfer name"
                disabled={isInitiatingTransfer}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="effective_date">Date</Label>
              <Input
                id="effective_date"
                type="date"
                value={transferFormData.effective_date}
                onChange={(event) =>
                  setTransferFormData((previous) => ({
                    ...previous,
                    effective_date: event.target.value,
                  }))
                }
                disabled={isInitiatingTransfer}
              />
              <p className="text-xs italic text-muted-foreground">
                Date of Transfer of Capital Account Position
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transfer_type">Type of Transfer</Label>
              <Select
                value={transferFormData.transfer_type}
                onValueChange={(value) =>
                  handleTransferTypeChange(value as 'full' | 'partial')
                }
                disabled={isInitiatingTransfer}
              >
                <SelectTrigger id="transfer_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Transfer</SelectItem>
                  <SelectItem value="partial">Partial Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <section className="space-y-4">
              <div className="space-y-1">
                <h3 className="text-base font-semibold">Transferor Details</h3>
                <p className="text-sm text-muted-foreground">
                  Investor entity transferring its capital account position.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="transferor_investor">
                  Select Transferor Entity *
                </Label>
                <Select
                  value={transferringInvestor?.id || ''}
                  onValueChange={handleTransferorChange}
                  disabled={isInitiatingTransfer}
                >
                  <SelectTrigger id="transferor_investor">
                    <SelectValue placeholder="Select transferor investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {transferorCandidates.map((candidate) => (
                      <SelectItem key={candidate.id} value={candidate.id}>
                        {candidate.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="min-h-8 text-xs text-muted-foreground">
                  Select the investor currently holding the position in this
                  fund.
                </p>
              </div>

              <div className="overflow-hidden rounded-md border">
                <div className="grid grid-cols-2 border-b bg-muted/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span>Entity Name</span>
                  <span className="text-right">Total Commitment</span>
                </div>
                <div className="grid grid-cols-2 px-4 py-3 text-sm">
                  <span>{transferringInvestor?.name || '-'}</span>
                  <span className="text-right">
                    {formatCurrency(
                      transferringRelationship?.commitment_amount,
                      transferringRelationship?.commitment_currency ||
                        fund.base_currency
                    )}
                  </span>
                </div>
              </div>
            </section>

            <section className="space-y-4">
              <div className="space-y-1">
                <h3 className="text-base font-semibold">Transferee Details</h3>
                <p className="text-sm text-muted-foreground">
                  Investor entity receiving the transferred capital account
                  position.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="transferee_investor">
                  Select Transferee Entities *
                </Label>
                <SearchableSelect
                  id="transferee_investor"
                  options={transfereeOptions}
                  getOptionKey={(candidate) => candidate.id}
                  getOptionGroup={(candidate) =>
                    candidate.isExistingFundInvestor
                      ? 'In this fund'
                      : 'Other investors'
                  }
                  renderOption={(candidate) => (
                    <span className="truncate">{candidate.name}</span>
                  )}
                  onSelect={handleAddTransfereeAllocation}
                  onSearchChange={setTransfereeSearch}
                  debounceMs={0}
                  isLoading={investorsOutsideFundQuery.isLoading}
                  disabled={
                    selectableTransferees.length === 0 || isInitiatingTransfer
                  }
                  placeholder={
                    selectableTransferees.length === 0
                      ? 'No eligible transferee investors'
                      : 'Select transferee investor'
                  }
                  searchPlaceholder="Search entities..."
                  emptyMessage="No matching investor found."
                  promptMessage="No eligible transferee investors"
                />
                <p className="min-h-8 text-xs text-muted-foreground">
                  Investors already in this fund have the transferred position
                  merged into their existing commitment. Investors joining this
                  fund get a new relationship created from the transferor&apos;s
                  terms.
                </p>
              </div>

              <div className="overflow-hidden rounded-md border">
                <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_72px_36px] items-center gap-3 border-b bg-muted/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <span>Entity Name</span>
                  <span>Transfer Commitment</span>
                  <span className="text-right">%</span>
                  <span className="sr-only">Actions</span>
                </div>

                {transferFormData.transferee_allocations.length === 0 ? (
                  <div className="px-4 py-6 text-sm text-muted-foreground">
                    No transferee allocations added yet.
                  </div>
                ) : (
                  transferFormData.transferee_allocations.map((allocation) => {
                    const transfereeInvestor = availableTransferees.find(
                      (candidate) =>
                        candidate.id === allocation.transferee_investor_id
                    );
                    const allocationAmountNumeric =
                      allocation.transfer_commitment_amount ?? 0;
                    const allocationPercentage =
                      normalizedTransferorCommitmentAmountNumeric
                        ? (
                            (Math.min(
                              allocationAmountNumeric || 0,
                              normalizedTransferorCommitmentAmountNumeric
                            ) /
                              normalizedTransferorCommitmentAmountNumeric) *
                            100
                          ).toFixed(2)
                        : '0.00';

                    const otherAllocationsTotal =
                      transferFormData.transferee_allocations
                        .filter(
                          (candidate) =>
                            candidate.transferee_investor_id !==
                            allocation.transferee_investor_id
                        )
                        .map(
                          (candidate) =>
                            candidate.transfer_commitment_amount ?? 0
                        )
                        .filter((amount) => amount > 0)
                        .reduce((sum, amount) => sum + amount, 0);

                    const maxAllowedAmount = Number.isFinite(
                      normalizedTransferorCommitmentAmountNumeric
                    )
                      ? Math.max(
                          normalizedTransferorCommitmentAmountNumeric -
                            otherAllocationsTotal,
                          0
                        )
                      : undefined;

                    return (
                      <div
                        key={allocation.transferee_investor_id}
                        className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_72px_36px] items-center gap-3 border-b px-4 py-3 last:border-b-0"
                      >
                        <div className="min-w-0">
                          <span className="block truncate text-sm">
                            {transfereeInvestor?.name || 'Unknown transferee'}
                          </span>
                          {transfereeInvestor &&
                            !transfereeInvestor.isExistingFundInvestor && (
                              <span className="block text-[11px] text-muted-foreground">
                                Joining this fund
                              </span>
                            )}
                        </div>
                        <CurrencyInput
                          value={allocation.transfer_commitment_amount}
                          onChange={(value) =>
                            handleTransferAmountChange(
                              allocation.transferee_investor_id,
                              value
                            )
                          }
                          max={maxAllowedAmount}
                          currency={
                            transferringRelationship?.commitment_currency ||
                            fund.base_currency ||
                            'EUR'
                          }
                          decimalPlaces={0}
                          placeholder="Enter amount"
                          disabled={isInitiatingTransfer}
                        />
                        <span className="text-right text-sm">
                          {allocationPercentage}%
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            handleRemoveTransfereeAllocation(
                              allocation.transferee_investor_id
                            )
                          }
                          disabled={isInitiatingTransfer}
                          aria-label={`Remove allocation for ${transfereeInvestor?.name || 'selected investor'}`}
                          title="Remove allocation"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })
                )}

                <div className="flex items-center justify-between border-t bg-muted/20 px-4 py-2 text-xs text-muted-foreground">
                  <span>
                    Total allocation:{' '}
                    {normalizedTransferorCommitmentAmountNumeric > 0
                      ? (
                          (Math.min(
                            totalAllocatedCommitmentAmountNumeric,
                            normalizedTransferorCommitmentAmountNumeric
                          ) /
                            normalizedTransferorCommitmentAmountNumeric) *
                          100
                        ).toFixed(2)
                      : '0.00'}
                    %
                  </span>
                  <span>
                    Remaining:{' '}
                    {formatCurrency(
                      remainingTransferAmountNumeric,
                      transferringRelationship?.commitment_currency ||
                        fund.base_currency
                    )}
                  </span>
                </div>
              </div>
            </section>
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isInitiatingTransfer}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={
              isInitiatingTransfer ||
              transferFormData.transferee_allocations.length === 0 ||
              loading ||
              !isTransferConfigured
            }
          >
            {isInitiatingTransfer ? 'Processing...' : 'Continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
